import { withAuthenticationRequired } from "@auth0/auth0-react";
import Loading from "../components/common/Loading";
import React, { useContext, useEffect, useState } from "react";
import { Grid } from "@mui/material";
import { useReservationAPI } from "../service/useReservationAPI";
import { toLocalDate } from "../common/DateUtil";
import CheckInDate from "./reservationFlow/CheckInDate";
import CheckOutDate from "./reservationFlow/CheckOutDate";
import SelectStay from "./reservationFlow/SelectStay";
import CheckInHour from "./reservationFlow/CheckInHour";
import CheckOutHour from "./reservationFlow/CheckOutHour";
import { GlobalStateContext } from "../state/GlobalStateProvider";
import AppSnackbar from "../common/AppSnackbar";
import { gql, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";

const GET_CONFIGURATION_PARAMETER = gql`
  query ConfiguratieParameterByType($type: ConfigurationParameterType!) {
    configuratieParameterByType(type: $type) {
      id
      startDate
      endDate
      amount
      type
    }
  }
`;

function NewReservationPlanner({ updateCurrentStep }) {
  const { t } = useTranslation();
  const { globalState, updateGlobalState } = useContext(GlobalStateContext);
  const { data } = useQuery(GET_CONFIGURATION_PARAMETER, {
    variables: { type: "BOOKING_OPEN_DATE" },
  });
  const [isLoading, setIsLoading] = useState(true);

  const {
    checkReservationAvailability,
    getAvailableStays,
    getPickUpDays,
    getDeliverDays,
    createReservation,
    updateReservation,
  } = useReservationAPI();

  const [availableStays, setAvailableStays] = useState([]);
  const [pickUpDays, setPickUpDays] = useState([]);
  const [deliverDays, setDeliverDays] = useState([]);
  const [checkedStays, setCheckedStays] = useState([]);
  const [currentStep, setCurrentStep] = useState("STEP_1");

  // Determine reservationStart: if configured startDate is before today, use today instead
  const configuredStart = data?.configuratieParameterByType?.[0]?.startDate
    ? new Date(data.configuratieParameterByType[0].startDate)
    : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const startCandidate = configuredStart ? new Date(configuredStart) : today;
  startCandidate.setHours(0, 0, 0, 0);
  const reservationStart = startCandidate < today ? today : startCandidate;

  const reservationEnd = new Date(
    data?.configuratieParameterByType?.[0]?.endDate,
  );

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  function handleFetchError(reason) {
    setSnackbarMessage(reason);
    setOpenSnackbar(true);
  }

  useEffect(() => {
    if (globalState.reservationMeta.toDate) {
      checkAvailability();
    }
    setIsLoading(false);
  }, []);

  function checkAvailability() {
    setIsLoading(true);
    checkReservationAvailability(
      toLocalDate(globalState.reservationMeta.fromDate),
      toLocalDate(globalState.reservationMeta.toDate),
      globalState.reservationMeta.animalsInReservationIds.length,
    )
      .then((isAvailable) => {
        if (isAvailable) {
          // promise.all ensures the final block to set loading is waiting to these calls to be completed
          return Promise.all([
            getAvailableStays(
              toLocalDate(globalState.reservationMeta.fromDate),
              toLocalDate(globalState.reservationMeta.toDate),
              globalState.reservationMeta.animalsInReservationIds.length,
            )
              .then((value) => setAvailableStays(value))
              .catch(() => handleFetchError(t("fetchAvailableStaysFailed"))),

            getPickUpDays(toLocalDate(globalState.reservationMeta.toDate))
              .then((value) => setPickUpDays(value))
              .catch(() =>
                handleFetchError(t("checkAvailabilityCheckInHoursFailed")),
              ),

            getDeliverDays(toLocalDate(globalState.reservationMeta.fromDate))
              .then((value) => setDeliverDays(value))
              .catch(() =>
                handleFetchError(t("checkAvailabilityCheckOutHoursFailed")),
              ),
          ]);
        }
      })
      .catch(() => handleFetchError(t("checkAvailabilityFailed")))
      .finally(() => setIsLoading(false));
  }

  const updateStays = (updatedStays) => {
    setCheckedStays(updatedStays);
  };

  const updateReservationStep = (step) => {
    setCurrentStep("STEP_" + step);
    setAvailableStays([]);

    if (step === 3) {
      checkAvailability();
    }

    if (step <= -1) {
      updateCurrentStep(1);
    }
  };

  const handleReserve = () => {
    setIsLoading(true);
    if (globalState.reservationUuid) {
      updateReservation(
        globalState.reservationUuid,
        globalState.reservationMeta,
      )
        .then((reservationUuid) => {
          updateGlobalState({ reservationUuid: reservationUuid });
          updateGlobalState({
            lastSelectedDateInCalender: globalState.reservationMeta.fromDate,
          });
          updateCurrentStep(3);
        })
        .catch(() => handleFetchError(t("updateReservationFailed")))
        .finally(() => setIsLoading(false));
    } else {
      createReservation(globalState.reservationMeta)
        .then((reservationUuid) => {
          updateGlobalState({ reservationUuid: reservationUuid });
          updateGlobalState({
            lastSelectedDateInCalender: globalState.reservationMeta.fromDate,
          });
          updateCurrentStep(3);
        })
        .catch(() => handleFetchError(t("createReservationFailed")))
        .finally(() => setIsLoading(false));
    }
  };

  return (
    <Grid
      container
      justifyContent="center"
      sx={{ textAlign: "center", marginLeft: 2 }}
    >
      {isLoading && <Loading />}
      {!isLoading && (
        <Grid item xs={8}>
          {currentStep === "STEP_1" && (
            <CheckInDate
              reservationStart={reservationStart}
              isLoadingInParent={isLoading}
              reservationEnd={reservationEnd}
              updateReservationStep={updateReservationStep}
            />
          )}

          {currentStep === "STEP_2" && (
            <CheckOutDate
              reservationEnd={reservationEnd}
              isLoadingInParent={isLoading}
              updateReservationStep={updateReservationStep}
            />
          )}

          {currentStep === "STEP_3" && (
            <SelectStay
              availableStays={availableStays}
              checkedStays={checkedStays}
              isLoadingInParent={isLoading}
              updateStays={updateStays}
              updateReservationStep={updateReservationStep}
            />
          )}

          {currentStep === "STEP_4" && (
            <CheckInHour
              checkInHours={deliverDays}
              isLoadingInParent={isLoading}
              updateReservationStep={updateReservationStep}
            />
          )}

          {currentStep === "STEP_5" && (
            <CheckOutHour
              checkOutHours={pickUpDays}
              isLoadingInParent={isLoading}
              updateReservationStep={updateReservationStep}
              handleReserve={handleReserve}
            />
          )}
          {/*    TODO update flow doorheen reservatie checken --> staytype wordt niet geupdate waardoor prijs niet meer klopt, alles nakijken om finaal te geraken voor focus op backend!*/}
        </Grid>
      )}
      <AppSnackbar
        openSnackbar={openSnackbar}
        snackbarMessage={snackbarMessage}
        setOpenSnackbar={setOpenSnackbar}
      />
    </Grid>
  );
}

export default withAuthenticationRequired(NewReservationPlanner, {
  onRedirecting: () => <Loading />,
  returnTo: "/nieuwe-reservatie",
});
